import { useLocation } from "react-router";
import { BlockTitle } from "@blocks/Blocks";
import Container from "@components/Container/Container";
import SplideArrows from "@components/Slider/components/SplideArrows";
import useSendDataToGTM from "@features/analytics/hooks/useSendDataToGTM";
import {
  Options,
  Splide,
  SplideSlide,
  SplideTrack,
} from "@greglaisph/react-splide";
import { FeedArticleItems } from "@routes/Article/utils/types";
import { Grid } from "@splidejs/splide-extension-grid";
import { cn } from "@util/helpers";

import PodcastComingUpNextCard from "./PodcastComingUpNextCard";

type PodcastComingUpNextComponentProps = {
  /** Podcast coming up next articles */
  podcastsComingUpNextData: FeedArticleItems[];
  /** Flag to indicate if the title should be displayed */
  displayTitle?: boolean;
  /** Additional class name for the component */
  className?: string;
  /** Title of the currect article */
  title: string;
  /** Flag to check if carousel is from about section. */
  isFromAbout?: boolean;
};

/**
 * Component for Podcast Coming up next Section.
 * https://www.figma.com/design/xsbEGttNPBTNUi388qtSJD/PODCASTS--WEB-?node-id=4844-133247&m=dev
 */
const PodcastComingUpNextComponent = ({
  podcastsComingUpNextData = [],
  displayTitle = false,
  className,
  title,
  isFromAbout = false,
}: PodcastComingUpNextComponentProps): React.ReactElement => {
  const sendDataToGTM = useSendDataToGTM();
  const { pathname } = useLocation();

  if (podcastsComingUpNextData.length <= 0) return <></>;

  const options: Options = {
    pagination: false,
    type: "slide",
    autoplay: false,
    interval: 10000,
    perMove: 2,
    focus: 0,
    omitEnd: true,
    mediaQuery: "min",
    perPage: 1.5,
    arrows: false,
    breakpoints: {
      768: { perPage: 3, perMove: 3, fixedWidth: undefined },
      992: { arrows: true },
      1200: { perPage: 4, perMove: 4 },
    },
  };

  const trackingObject = {
    event: "custom_event",
    eventCategory: "podcast",
    eventAction: "scroll::carousel::coming up next",
    eventLabel: title,
  };

  const handleOnClickSendData = () => {
    if (typeof window === "undefined") return;
    if (!trackingObject) return;

    if (trackingObject?.eventLabel === undefined) {
      trackingObject.eventLabel = pathname;
    }

    sendDataToGTM(trackingObject);
  };

  return (
    <Container
      data-testid="podcast-coming-up-next-component"
      rootClassName={cn("mb-4 px-4 pb-6 md:px-0", className)}
    >
      <>
        {displayTitle ? (
          <BlockTitle
            text="coming up next"
            rootClassName="font-poppins mb-6 text-2xs lg:text-xs font-normal uppercase text-gray-650"
          />
        ) : null}
      </>

      <Splide
        options={options}
        extensions={{ Grid }}
        tag="div"
        hasTrack={false}
        className="relative lg:pr-9"
      >
        <SplideTrack>
          {podcastsComingUpNextData?.map((podcast, index) => {
            return (
              <SplideSlide key={podcast.id} className="!mr-0">
                <PodcastComingUpNextCard
                  podcastArticle={podcast}
                  index={index}
                  isFromAbout={isFromAbout}
                />
              </SplideSlide>
            );
          })}
        </SplideTrack>

        <SplideArrows
          className="absolute bottom-0 right-0 top-0 flex flex-col items-center justify-center gap-2"
          prevArrowClassName="min-h-9 max-h-9 !border-gray-175"
          nextArrowClassName="min-h-9 max-h-9 !border-gray-175"
          onClick={handleOnClickSendData}
        />
      </Splide>
    </Container>
  );
};

export default PodcastComingUpNextComponent;
